import React, {createContext, useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {matchPath, useLocation} from 'react-router-dom';
import {ROUTE_CONFIG} from 'constants/app-routes';

/**
 * Context to manage the state of multi-select functionality for facilities.
 * This context provides a boolean value indicating whether multi-select is enabled.
 */
export const FacilityMultisSelectContext = createContext();

/**
 * Custom hook to access the FacilityMultisSelectContext.
 *
 * @returns {boolean} The current value of the multiSelectEnabled state from the FacilityMultisSelectContext.
 */
export const useFacilityMultiSelect = () =>
    useContext(FacilityMultisSelectContext);

/**
 * Provides the FacilityMultiSelect context to its children.
 * It uses the `useLocation` hook to get the current location and sets the `multiSelectEnabled` state based on the route configuration.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The provider component that supplies the `multiSelectEnabled` state to its children.
 */
export const FacilityMultiSelectProvider = ({children}) => {
    const location = useLocation();
    const [multiSelectEnabled, setMultiSelectEnabled] = useState(true);

    useEffect(
        function toggleFacilityMultiSelectEffect() {
            for (const route of ROUTE_CONFIG) {
                if (!route.name) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const match = matchPath(location.pathname, {
                    path: route.path,
                    exact: true,
                });

                if (match) {
                    setMultiSelectEnabled(
                        typeof route.isMultiFacility === 'function'
                            ? route.isMultiFacility(match)
                            : false
                    );

                    break;
                }
            }
        },
        [location.pathname]
    );

    return (
        <FacilityMultisSelectContext.Provider value={multiSelectEnabled}>
            {children}
        </FacilityMultisSelectContext.Provider>
    );
};

FacilityMultiSelectProvider.propTypes = {
    children: PropTypes.node,
};
